import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { PublicLayout } from "../../components/PublicLayout";
import { FloatingDecor, SectionTitle } from "../../components/Decor";
import { ProductCard } from "../../components/ProductCard";
import { BestsellerCarousel } from "../../components/BestsellerCarousel";
import { HeroBannerCarousel } from "../../components/HeroBannerCarousel";
import { api, MXN } from "../../lib/apiClient";
import { categoryIcon } from "../../lib/icons";
import { Sparkles, Cake, CalendarHeart, MessageCircle, Star, PartyPopper, Search, ArrowRight } from "lucide-react";

const steps = [
  { icon: Cake, title: "Elige lo que se te antoja", text: "Explora el catálogo y encuentra tu dulce favorito." },
  { icon: Sparkles, title: "Arma tu pedido", text: "Personaliza sabores, rellenos, tamaño y decoración." },
  { icon: CalendarHeart, title: "Elige fecha y entrega", text: "Selecciona el día, la hora y cómo lo recibirás." },
  { icon: MessageCircle, title: "Confirmamos por WhatsApp", text: "Te contactamos para afinar detalles y pago." },
];
const testimonios = [
  { name: "Ana L.", text: "El pastel quedó hermoso y delicioso. ¡Todos preguntaron dónde lo pedí!", stars: 5 },
  { name: "Majo R.", text: "Los roles de canela son otro nivel. Se volvieron mi antojo semanal.", stars: 5 },
  { name: "Carlos M.", text: "La mesa de postres del evento fue un éxito total. Súper atentas.", stars: 5 },
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data.slice(0, 8))).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const promo = products.find((p) => p.tags?.includes("mas-vendido") && !p.requires_quote)
    || products.find((p) => !p.requires_quote) || products[0];
  const featured = products.slice(0, 8);
  const bestsellers = (() => {
    const tagged = products.filter((p) => p.tags?.includes("mas-vendido"));
    return (tagged.length >= 3 ? tagged : products).slice(0, 6);
  })();
  const heroBanners = (() => {
    const configured = (settings.hero_banners || []).filter((b) => b?.enabled !== false && b?.title);
    if (configured.length) return configured;
    if (!promo) return [];
    return [{
      id: "fallback-promo",
      eyebrow: "Recién horneado",
      subtitle: "Lo más rico",
      title: promo.name,
      price_text: promo.requires_quote ? "Cotización" : `Desde ${MXN(promo.base_price)}`,
      image: promo.images?.[0],
      cta_label: "Pedir ahora",
      product_id: promo.id,
      enabled: true,
    }];
  })();

  return (
    <PublicLayout>
      {/* Greeting + Hero */}
      <section className="relative overflow-hidden">
        <FloatingDecor />
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-rosa/25 blur-3xl" />
        <div className="absolute top-40 -left-24 w-96 h-96 rounded-full bg-dorado/12 blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-5 sm:px-6 pt-8 sm:pt-12">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="font-serif italic text-vino/70 text-lg">Hola, Kelifalovers 🍒</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="font-serif text-4xl sm:text-6xl text-vino tracking-tight leading-[1.02] mt-1">
            ¿Qué se te antoja hoy?
          </motion.h1>

          {/* Search */}
          <button onClick={() => navigate("/catalogo")} data-testid="home-search"
            className="mt-5 w-full max-w-xl flex items-center gap-3 bg-blancoCalido rounded-full py-4 px-5 shadow-[0_10px_30px_rgba(118,27,56,0.08)] border border-beige/60 text-cafe/40 hover:shadow-[0_14px_36px_rgba(118,27,56,0.13)] transition-shadow">
            <Search className="w-5 h-5 text-vino" /> Buscar pasteles, roles, cupcakes…
          </button>
        </div>
      </section>

      <HeroBannerCarousel banners={heroBanners} />

      {/* Category pills */}
      <section className="max-w-6xl mx-auto px-5 sm:px-6 mt-8">
        <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2 -mx-1 px-1">
          <button onClick={() => navigate("/catalogo")} className="flex items-center gap-2 bg-vino text-crema px-5 py-3 rounded-full font-medium whitespace-nowrap shadow-md text-sm" data-testid="home-cat-todos">
            <Sparkles className="w-4 h-4" /> Todo
          </button>
          {categories.map((c) => {
            const Icon = categoryIcon(c.icon);
            return (
              <button key={c.id} onClick={() => navigate("/catalogo")} data-testid={`home-cat-${c.id}`}
                className="flex items-center gap-2 bg-blancoCalido text-cafe/80 px-5 py-3 rounded-full font-medium whitespace-nowrap border border-beige shadow-sm hover:bg-beige/60 transition-colors text-sm">
                <Icon className="w-4 h-4 text-vino" /> {c.name}
              </button>
            );
          })}
        </div>
      </section>

      {/* Bestsellers carousel */}
      <BestsellerCarousel products={bestsellers} />

      {/* Featured grid */}
      <section className="max-w-6xl mx-auto px-5 sm:px-6 py-8">
        <div className="flex items-end justify-between mb-5">
          <div>
            <p className="font-serif italic text-vino/60">lo más rico</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-vino tracking-tight">Nuestros consentidos</h2>
          </div>
          <button onClick={() => navigate("/catalogo")} data-testid="ver-catalogo-btn" className="hidden sm:flex items-center gap-1 text-vino font-medium hover:gap-2 transition-all">Ver todo <ArrowRight className="w-4 h-4" /></button>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
        <div className="text-center mt-8 sm:hidden">
          <button onClick={() => navigate("/catalogo")} className="border-2 border-vino text-vino rounded-full px-8 py-3.5 font-medium">Ver todo el catálogo</button>
        </div>
      </section>

      {/* Cómo pedir */}
      <section className="bg-blancoCalido border-y border-beige py-14">
        <div className="max-w-6xl mx-auto px-5 sm:px-6">
          <SectionTitle sub="Pedir tu dulce favorito es súper fácil y rápido.">Cómo pedir</SectionTitle>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {steps.map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="bg-crema rounded-[28px] p-6 border border-beige text-center shadow-pastel">
                <div className="w-14 h-14 mx-auto rounded-full bg-vino/10 flex items-center justify-center mb-4"><s.icon className="w-7 h-7 text-vino" /></div>
                <p className="font-serif text-xl text-vino mb-1">{i + 1}. {s.title}</p>
                <p className="text-sm text-cafe/60">{s.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Servicios */}
      <section className="max-w-6xl mx-auto px-5 sm:px-6 py-14">
        <div className="rounded-[36px] bg-gradient-to-br from-vino to-vinoDark text-crema p-8 sm:p-12 relative overflow-hidden">
          <PartyPopper className="absolute right-6 top-6 w-24 h-24 text-crema/10" />
          <h2 className="font-serif text-3xl sm:text-4xl mb-3">Servicios para tus eventos</h2>
          <p className="text-crema/80 max-w-lg mb-6">Barras dulces, snack bar, barra de roles, barra de bebidas y mesas de postres. Diseñamos una experiencia dulce a la medida de tu celebración.</p>
          <button onClick={() => navigate("/servicios")} data-testid="cta-servicios-btn" className="bg-crema text-vino rounded-full px-7 py-3.5 font-medium hover:bg-white transition-all hover:-translate-y-0.5">Solicitar cotización</button>
        </div>
      </section>

      {/* Testimonios */}
      <section className="max-w-6xl mx-auto px-5 sm:px-6 pb-6">
        <SectionTitle>Lo que dicen de Kelifa</SectionTitle>
        <div className="grid sm:grid-cols-3 gap-5">
          {testimonios.map((t, i) => (
            <div key={i} className="bg-blancoCalido rounded-[28px] p-6 border border-beige shadow-pastel">
              <div className="flex gap-0.5 mb-3">{Array.from({ length: t.stars }).map((_, s) => <Star key={s} className="w-4 h-4 fill-dorado text-dorado" />)}</div>
              <p className="text-cafe/70 text-sm italic">"{t.text}"</p>
              <p className="mt-4 font-medium text-vino text-sm">{t.name}</p>
            </div>
          ))}
        </div>
      </section>
    </PublicLayout>
  );
}
